import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { Icon } from 'semantic-ui-react';
import toast from 'react-hot-toast';
import {
  AnswerPayload,
  BuyerProfileAnswerEntry,
  FORM_SECTION_BLURBS,
  FORM_SECTION_LABELS,
  FORM_SECTION_ORDER,
  FormSectionKey,
  QuestionConfig,
  QuestionDef,
} from './types';
import {
  AnswersMap,
  TimestampMap,
  answerKey,
  answersByKeyForDisplay,
  dropUnknownQuestions,
  entriesToMap,
  entriesToTimestampMap,
  isQuestionAnswered,
  latestTimestampForQuestion,
  mapToEntries,
  migrateLegacyNullSegmentAnswers,
  timestampRange,
} from './answersState';
import { isQuestionVisible, visibleOptionsForQuestion } from './displayRules';
import QuestionCard from './QuestionCard';
import './styles.scss';

const AUTOSAVE_DELAY_MS = 1500;

interface Props {
  config: QuestionConfig;
  initialAnswers: BuyerProfileAnswerEntry[];
  readOnly?: boolean;
  /** Persists the answers and resolves with the saved entries (with timestamps). */
  onSave: (answers: BuyerProfileAnswerEntry[]) => Promise<BuyerProfileAnswerEntry[]>;
}

const BuyerProfileForm: React.FC<Props> = ({
  config,
  initialAnswers,
  readOnly,
  onSave,
}) => {
  const [answers, setAnswers] = useState<AnswersMap>(() =>
    migrateLegacyNullSegmentAnswers(
      dropUnknownQuestions(entriesToMap(initialAnswers), config.questions),
      config.questions
    )
  );
  const [timestamps, setTimestamps] = useState<TimestampMap>(() =>
    entriesToTimestampMap(initialAnswers)
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  // Collapsed category sections (all collapsed by default).
  const [collapsed, setCollapsed] = useState<Set<FormSectionKey>>(
    () => new Set(FORM_SECTION_ORDER)
  );
  const toggleSection = (key: FormSectionKey) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sortedQuestions = useMemo(
    () => [...config.questions].sort((a, b) => a.order - b.order),
    [config.questions]
  );

  // Maps a market-segment option value to its label, for per-segment headings.
  const segmentLabels = useMemo(() => {
    const q = config.questions.find((x) => x.key === 'market_segment');
    const map: Record<string, string> = {};
    (q?.options ?? []).forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [config.questions]);

  // Maps a US state code to its name, for the utility-zones group sub-headings.
  const stateLabels = useMemo(() => {
    const q = config.questions.find((x) => x.key === 'geography');
    const map: Record<string, string> = {};
    (q?.options ?? []).forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [config.questions]);

  const flushSave = useCallback(
    async (map: AnswersMap) => {
      const toastId = toast.loading('Saving…');
      try {
        const saved = await onSave(mapToEntries(map));
        setTimestamps(entriesToTimestampMap(saved));
        toast.success('All changes saved', { id: toastId });
      } catch {
        toast.error('Could not save — your last change will retry.', { id: toastId });
      }
    },
    [onSave]
  );

  // Debounced autosave. Skips the initial render (dirtyRef gates it).
  useEffect(() => {
    if (!dirtyRef.current || readOnly) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void flushSave(answers), AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [answers, flushSave, readOnly]);

  const updateAnswer = (
    questionKey: string,
    segment: string | null,
    answer: AnswerPayload
  ) => {
    dirtyRef.current = true;
    setAnswers((prev) => ({ ...prev, [answerKey(questionKey, segment)]: answer }));
  };

  const displayInputs = useMemo(
    () => answersByKeyForDisplay(answers, config.questions),
    [answers, config.questions]
  );

  // Span of the questions' "last updated" dates — a glanceable freshness gauge.
  const freshness = useMemo(() => timestampRange(timestamps), [timestamps]);
  const fmt = (iso: string) => format(new Date(iso), 'MMM d, yyyy');

  // A question is shown when its display rule passes AND — for questions with
  // an `optionGroupFilter` (utility zones) — at least one option survives the
  // filter. The latter hides the utility-zones panel until the buyer has
  // selected a state with zones, completing the "state AND Community Solar"
  // gate from issue #1908.
  const visibleQuestions = useMemo(
    () =>
      sortedQuestions.filter(
        (q) =>
          isQuestionVisible(q, displayInputs) &&
          (!q.optionGroupFilter ||
            visibleOptionsForQuestion(q, displayInputs).length > 0)
      ),
    [sortedQuestions, displayInputs]
  );

  // Nested children (questions with `nestUnder`) are folded into their
  // parent's card rather than getting their own top-level row. Filter them
  // out of the main list and hand each parent its visible children.
  const topLevelQuestions = useMemo(
    () => visibleQuestions.filter((q) => !q.nestUnder),
    [visibleQuestions]
  );
  const nestedChildrenByParent = useMemo(() => {
    const map: Record<string, QuestionDef[]> = {};
    for (const q of visibleQuestions) {
      if (!q.nestUnder) continue;
      (map[q.nestUnder.question] ??= []).push(q);
    }
    return map;
  }, [visibleQuestions]);

  // Group the top-level questions into form sections that mirror the match
  // scoring axes (one section per category, `notes` falling into `other`).
  // Questions keep their `order` within a section; empty sections drop out.
  const sections = useMemo(() => {
    const byKey = new Map<FormSectionKey, QuestionDef[]>();
    for (const q of topLevelQuestions) {
      // A `formSection` override wins (e.g. ITC questions); otherwise scored
      // questions group by category and the rest fall into `signal`.
      const key: FormSectionKey = q.formSection ?? q.category ?? 'signal';
      const list = byKey.get(key) ?? [];
      list.push(q);
      byKey.set(key, list);
    }
    return FORM_SECTION_ORDER.map((key) => ({
      key,
      questions: byKey.get(key) ?? [],
    })).filter((s) => s.questions.length > 0);
  }, [topLevelQuestions]);

  const questionDomId = (key: string) => `bp-question-${key}`;
  const sectionDomId = (key: FormSectionKey) => `bp-section-${key}`;

  // Most recent "last updated" across a section's questions (and their nested
  // children, which share the section's category) — shown in the header so a
  // collapsed section still surfaces its freshness.
  const sectionUpdatedAt = (questions: QuestionDef[]): string | undefined => {
    let latest: string | undefined;
    for (const q of questions) {
      for (const def of [q, ...(nestedChildrenByParent[q.key] ?? [])]) {
        const ts = latestTimestampForQuestion(timestamps, def.key);
        if (ts && (!latest || ts > latest)) latest = ts;
      }
    }
    return latest;
  };

  // Count of a section's top-level questions still awaiting input — drives the
  // "needs input" cue in the header so a collapsed section still signals it has
  // unanswered questions. Suppressed in read-only mode (nothing to fill in).
  const sectionUnansweredCount = (questions: QuestionDef[]): number =>
    readOnly ? 0 : questions.filter((q) => !isQuestionAnswered(q, answers)).length;

  const scrollTo = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // TOC links may target a collapsed section. Expand it first, then scroll on
  // the next frame once the question rows have rendered.
  const revealQuestion = (sectionKey: FormSectionKey, questionKey: string) => {
    setCollapsed((prev) => {
      if (!prev.has(sectionKey)) return prev;
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
    requestAnimationFrame(() => scrollTo(questionDomId(questionKey)));
  };

  return (
    <div className="buyer-profile-form">
      <div className="card buyer-profile-heading-card">
        {freshness && (
          <span className="buyer-profile-heading-updated">
            Last updated {fmt(freshness.latest)}
          </span>
        )}
        <h2>Buyer Profile</h2>
        <p className="buyer-profile-subheading">
          Tell us what deals you&apos;re looking for. Every question is optional and
          your answers save automatically.
        </p>
      </div>

      <div className="buyer-profile-layout">
        <div className="buyer-profile-questions">
          {sections.map((section) => {
            const isCollapsed = collapsed.has(section.key);
            const sectionUpdated = sectionUpdatedAt(section.questions);
            const unanswered = sectionUnansweredCount(section.questions);
            return (
              <div
                key={section.key}
                id={sectionDomId(section.key)}
                className="buyer-profile-section card"
              >
                <button
                  type="button"
                  className="buyer-profile-section-header"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleSection(section.key)}
                >
                  <Icon
                    name={isCollapsed ? 'chevron right' : 'chevron down'}
                    className="buyer-profile-section-chevron"
                  />
                  <div className="buyer-profile-section-header-text">
                    <div className="buyer-profile-section-heading-row">
                      <h3 className="buyer-profile-section-heading">
                        {FORM_SECTION_LABELS[section.key]}
                      </h3>
                      {unanswered > 0 && (
                        <span
                          className="buyer-profile-section-flag"
                          title={`${unanswered} ${
                            unanswered === 1 ? 'question' : 'questions'
                          } still need input`}
                        >
                          <Icon
                            name="circle"
                            className="buyer-profile-section-flag-dot"
                          />
                          Needs input
                        </span>
                      )}
                    </div>
                    <p className="buyer-profile-section-blurb">
                      {FORM_SECTION_BLURBS[section.key]}
                    </p>
                  </div>
                  {sectionUpdated && (
                    <span className="buyer-profile-section-updated">
                      Last updated {fmt(sectionUpdated)}
                    </span>
                  )}
                </button>
                {!isCollapsed &&
                  section.questions.map((question) => (
                    <QuestionCard
                      key={question.key}
                      question={question}
                      answers={answers}
                      segmentLabels={segmentLabels}
                      answersByKey={displayInputs}
                      groupLabels={stateLabels}
                      readOnly={readOnly}
                      domId={questionDomId(question.key)}
                      updatedAt={latestTimestampForQuestion(timestamps, question.key)}
                      onChange={updateAnswer}
                      nestedChildren={nestedChildrenByParent[question.key]}
                    />
                  ))}
              </div>
            );
          })}
        </div>

        <aside className="buyer-profile-toc card">
          <div className="buyer-profile-toc-heading">On this page</div>
          <ul className="buyer-profile-toc-list">
            {sections.map((section) => (
              <li key={section.key} className="buyer-profile-toc-group">
                <button
                  type="button"
                  className="buyer-profile-toc-section"
                  onClick={() => scrollTo(sectionDomId(section.key))}
                >
                  {FORM_SECTION_LABELS[section.key]}
                </button>
                <ul className="buyer-profile-toc-list">
                  {section.questions.map((question) => (
                    <li key={question.key}>
                      <button
                        type="button"
                        className="buyer-profile-toc-link"
                        onClick={() => revealQuestion(section.key, question.key)}
                      >
                        {question.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
};

export default BuyerProfileForm;
